import React from 'react';
import { Modal } from 'antd';

import { GLOBAL_SHORTCUTS, keyGlyph, useShortcuts } from '../../contexts/ShortcutsContext';

/**
 * "Keyboard shortcuts" — the Help entry every page's ⋯ menu carries.
 *
 * What it lists is not a hand-kept document: the screens register their own
 * shortcuts as they mount (contexts/ShortcutsContext), so this shows what
 * works HERE, now, with the shortcuts that work anywhere underneath. A screen
 * that has none of its own still opens the modal — the global ones are worth
 * the trip, and an empty menu entry would be worse than a short list.
 *
 * One modal for the whole app, mounted once beside the provider.
 */

/** A combination as its keys: ⌘ ↵, each in its own cap. */
export function ShortcutKeys({ keys = [] }) {
	return (
		<span className="shortcuts__keys">
			{(keys || []).map((token, index) => (
				<kbd key={`${token}-${index}`} className="shortcuts__key">{keyGlyph(token)}</kbd>
			))}
		</span>
	);
}

export default function ShortcutsModal() {
	const shortcuts = useShortcuts();
	if (!shortcuts) return null;

	const { open, closeShortcuts, sections } = shortcuts;
	const screenSections = (sections || []).filter(section => (section.items || []).length);

	return (
		<Modal
			open={open}
			onCancel={closeShortcuts}
			footer={null}
			title="Keyboard shortcuts"
			width={520}
			className="shortcuts-modal"
		>
			{screenSections.map(section => (
				<section key={section.title} className="shortcuts__section">
					<div className="micro-label shortcuts__title">{section.title}</div>
					<dl className="shortcuts__list">
						{section.items.map((item, index) => (
							<div className="shortcuts__row" key={`${item.label}-${index}`}>
								<dt className="shortcuts__label">{item.label}</dt>
								<dd className="shortcuts__combo"><ShortcutKeys keys={item.keys} /></dd>
							</div>
						))}
					</dl>
				</section>
			))}

			<section className="shortcuts__section">
				<div className="micro-label shortcuts__title">Everywhere</div>
				<dl className="shortcuts__list">
					{GLOBAL_SHORTCUTS.map((item, index) => (
						<div className="shortcuts__row" key={`${item.label}-${index}`}>
							<dt className="shortcuts__label">{item.label}</dt>
							<dd className="shortcuts__combo"><ShortcutKeys keys={item.keys} /></dd>
						</div>
					))}
				</dl>
			</section>

			{/* Said once, at the foot: the list above is what this screen
			    offers, and a text field swallows the single-key ones. */}
			<p className="shortcuts__note">
				Single-key shortcuts work when you are not typing in a field.
			</p>
		</Modal>
	);
}
